import { useMemo, useState } from 'react';
import { formatEodSwap, formatNumber, pnlClass } from '@/utils/format';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { InstrumentInfoTooltip } from './tooltips';
import { EodSwapNetChild } from './EodSwapNetChild';
import type {
  EodNetInstrumentDto,
  RiskDashboardFilters,
  RiskDashboardInstrumentRowDto,
  RiskDashboardLoginDetailDto,
} from '@/api/types/riskDashboard';

const HEDGE: Record<string, { bg: string; label: string }> = {
  Balanced: { bg: 'bg-success-transparent', label: 'Balanced' },
  UnderHedged: { bg: 'bg-warning-transparent', label: 'Under-hedged' },
  OverHedged: { bg: 'bg-primary-transparent', label: 'Over-hedged' },
  WrongDirection: { bg: 'bg-danger-transparent', label: 'Wrong Direction' },
};

const ACTION: Record<string, { bg: string; label: string }> = {
  NoAction: { bg: 'bg-dark-transparent', label: 'NO ACTION' },
  Buy: { bg: 'bg-success-transparent', label: 'BUY' },
  Sell: { bg: 'bg-danger-transparent', label: 'SELL' },
};

export interface InstrumentFilterHandlers {
  onFilterLogin: (login: number, platformId?: number | null) => void;
  onFilterUniqueId: (uniqueId: string) => void;
}

// LP-only exposure columns, hidden when the Show LP mode is off (mirrors
// syncShowLp toggling .rd-lp-col in risk-dashboard-filter-chrome.js).
const LP_COLUMN_KEYS = new Set(['lpAvgPrice', 'lpNetLots', 'lpFloatingPnL', 'lpNotionalUSD']);

export function InstrumentsTab({
  rows,
  handlers,
  filters,
  eodBySymbol,
  showLp = true,
}: {
  rows: RiskDashboardInstrumentRowDto[];
  handlers: InstrumentFilterHandlers;
  filters: RiskDashboardFilters;
  eodBySymbol: Map<string, EodNetInstrumentDto>;
  showLp?: boolean;
}) {
  type R = RiskDashboardInstrumentRowDto;

  // Single open child row per table, in one of two modes (mirrors legacy
  // expandedSymbols[symbol].type: 'clients' = Open Positions via the chevron,
  // 'eod' = Expected-EOD-Swap Net child via the EOD-swap value click).
  const [open, setOpen] = useState<{ id: string; mode: 'clients' | 'eod' } | null>(null);
  const expandedKeys = useMemo(() => new Set(open ? [open.id] : []), [open]);

  const toggle = (id: string, mode: 'clients' | 'eod') =>
    setOpen((prev) => (prev && prev.id === id && prev.mode === mode ? null : { id, mode }));

  const allColumns: DataGridColumn<R>[] = [
    {
      key: 'sentiment',
      title: 'Sentiment',
      sortable: false,
      filterable: false,
      className: 'text-center',
      cell: (r) => {
        const longPct = r.longPct ?? 50;
        return (
          <div className="rd-sentiment-bar" title={`Long ${formatNumber(longPct, 0)}% / Short ${formatNumber(100 - longPct, 0)}%`}>
            <div className="rd-sentiment-long" style={{ width: `${longPct}%` }} />
            <div className="rd-sentiment-short" style={{ width: `${100 - longPct}%` }} />
          </div>
        );
      },
    },
    {
      key: 'symbol',
      title: 'Instrument',
      className: 'text-start',
      value: (r) => r.symbol,
      cell: (r) => (
        <span className="rd-instrument-cell">
          <span className="rd-instrument-name">{r.symbol}</span>
          <InstrumentInfoTooltip symbol={r.symbol} filters={filters} />
        </span>
      ),
    },
    {
      key: 'eodSwap',
      title: 'EOD swaps',
      className: 'text-end',
      value: (r) => eodBySymbol.get((r.symbol ?? '').toUpperCase())?.totalSwap ?? null,
      cell: (r) => {
        const eod = eodBySymbol.get((r.symbol ?? '').toUpperCase());
        const f = formatEodSwap(eod?.totalSwap ?? null);
        // External link to the full Expected-EOD-Swap view (legacy rd-eod-swap-open).
        const link = (
          <a
            href="/User/RiskMonitoring/ConsolidatedExposure#expected-eod-swap"
            className="rd-eod-swap-open ms-1"
            title="Open Expected EOD Swap on Net Open Position"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="ri-share-box-line align-middle" />
          </a>
        );
        // Click the value to expand the Net child row (legacy rd-eod-swap-btn → type:'eod').
        const value = (
          <button
            type="button"
            className={`btn btn-link p-0 rd-eod-swap-btn ${f.cls}`}
            title="Show Expected EOD Swap details"
            onClick={(e) => {
              e.stopPropagation();
              if (eod) toggle(r.id, 'eod');
            }}
          >
            {f.text}
          </button>
        );
        return (
          <span className="rd-eod-swap-cell">
            {value}
            {link}
          </span>
        );
      },
    },
    {
      key: 'concentration',
      title: 'CL Conc%',
      className: 'text-end',
      value: (r) => r.concentration ?? null,
      cell: (r) => (r.concentration == null ? '—' : `${formatNumber(r.concentration, 2)}%`),
    },
    { key: 'clientAvgPrice', title: 'CL Avg', className: 'text-end', value: (r) => r.clientAvgPrice ?? null, cell: (r) => formatNumber(r.clientAvgPrice, r.digits || 5) },
    { key: 'clientNetLots', title: 'CL Vol', className: 'text-end', value: (r) => r.clientNetLots, cell: (r) => formatNumber(r.clientNetLots, 2) },
    { key: 'lpAvgPrice', title: 'LP Avg', className: 'text-end', value: (r) => r.lpAvgPrice ?? null, cell: (r) => formatNumber(r.lpAvgPrice, r.digits || 5) },
    { key: 'lpNetLots', title: 'LP Vol', className: 'text-end', value: (r) => r.lpNetLots, cell: (r) => formatNumber(r.lpNetLots, 2) },
    { key: 'netVolume', title: 'Net Vol', className: 'text-end', value: (r) => r.netVolume, cell: (r) => formatNumber(r.netVolume, 2) },
    {
      key: 'matchStatus',
      title: 'Status',
      className: 'text-center',
      value: (r) => r.matchStatus,
      cell: (r) => {
        const h = HEDGE[r.matchStatus] ?? HEDGE.Balanced;
        return <span className={`badge ${h.bg}`}>{h.label}</span>;
      },
    },
    {
      key: 'tradeToBalance',
      title: 'Trade to Balance',
      className: 'text-end',
      value: (r) => r.tradeToBalance ?? 0,
      cell: (r) => {
        const a = ACTION[r.action] ?? ACTION.NoAction;
        return (
          <>
            {formatNumber(r.tradeToBalance ?? 0, 2)}
            <span className={`badge ${a.bg} ms-1`}>{a.label}</span>
          </>
        );
      },
    },
    { key: 'clientsFloatingPnL', title: 'CL PL', className: 'text-end', value: (r) => r.clientsFloatingPnL, cell: (r) => <span className={pnlClass(r.clientsFloatingPnL)}>{formatNumber(r.clientsFloatingPnL, 2)}</span> },
    { key: 'lpFloatingPnL', title: 'LP PnL', className: 'text-end', value: (r) => r.lpFloatingPnL, cell: (r) => <span className={pnlClass(r.lpFloatingPnL)}>{formatNumber(r.lpFloatingPnL, 2)}</span> },
    { key: 'netFloatingPnL', title: 'Net PnL', className: 'text-end', value: (r) => r.netFloatingPnL, cell: (r) => <span className={pnlClass(r.netFloatingPnL)}>{formatNumber(r.netFloatingPnL, 2)}</span> },
    { key: 'clientNotionalUSD', title: 'Cl Notional', className: 'text-end', value: (r) => r.clientNotionalUSD, cell: (r) => formatNumber(r.clientNotionalUSD, 0) },
    { key: 'lpNotionalUSD', title: 'LP Notional', className: 'text-end', value: (r) => r.lpNotionalUSD, cell: (r) => formatNumber(r.lpNotionalUSD, 0) },
  ];

  const columns = showLp ? allColumns : allColumns.filter((c) => !LP_COLUMN_KEYS.has(c.key));

  return (
    <div className="card custom-card mb-0">
      <div className="card-header justify-content-center">
        <div className="card-title text-center mb-0">
          <i className="fe fe-bar-chart-2 me-2 text-primary" />
          Instruments Exposure
        </div>
      </div>
      <div className="card-body p-0">
        <DataGrid
          columns={columns}
          data={rows}
          rowKey={(r) => r.id}
          columnFilters
          rowClassName={(r) => (r.isBreach ? 'threshold-breach' : undefined)}
          emptyMessage={
            <>
              Click <strong>Fetch</strong> for a snapshot, or turn on <strong>Live</strong> for realtime.
            </>
          }
          expandedKeys={expandedKeys}
          onExpandToggle={(r) => toggle(r.id, 'clients')}
          expandable={(r) => {
            if (open?.id === r.id && open.mode === 'eod') {
              const eod = eodBySymbol.get((r.symbol ?? '').toUpperCase());
              return eod ? <EodSwapNetChild eod={eod} /> : null;
            }
            return <OpenPositionsTable details={r.loginDetails} digits={r.digits || 2} handlers={handlers} eodBySymbol={eodBySymbol} />;
          }}
        />
      </div>
    </div>
  );
}

// Per-login Expected-EOD-Swap: long/short/total by the login's net-volume sign
// (mirrors legacy _loginEodSwapHtml), looked up from the instrument's EOD breakdown.
function loginEodSwap(eodBySymbol: Map<string, EodNetInstrumentDto> | undefined, d: RiskDashboardLoginDetailDto): number | null {
  const eod = eodBySymbol?.get((d.symbol ?? '').toUpperCase());
  if (!eod) return null;
  const list = d.source === 'LP' ? eod.lpEntities : eod.clientEntities;
  const ent = (list ?? []).find((e) => String(e.login) === String(d.login));
  if (!ent) return null;
  return d.netVolume > 0 ? ent.longSwap ?? null : d.netVolume < 0 ? ent.shortSwap ?? null : ent.totalSwap ?? null;
}

export function OpenPositionsTable({
  details,
  digits,
  handlers,
  showInstrument = false,
  emptyMessage = 'No data available',
  eodBySymbol,
}: {
  details: RiskDashboardLoginDetailDto[];
  digits: number;
  handlers?: InstrumentFilterHandlers;
  showInstrument?: boolean;
  emptyMessage?: string;
  eodBySymbol?: Map<string, EodNetInstrumentDto>;
}) {
  const cols = showInstrument ? 10 : 9;
  return (
    <div className="rd-child-panel">
      <div className="rd-open-positions-title">
        <i className="fe fe-layers" />
        Open Positions
      </div>
      <div className="rd-contributors-scroll">
        <table className="nop-inner-table table table-sm table-hover text-center my-1 mx-0 rd-contributors-table">
          <thead>
            <tr>
              <th>Source</th>
              {showInstrument && <th>Instrument</th>}
              <th>Login</th>
              <th>Unique ID</th>
              <th>Net Volume</th>
              <th>Avg Price</th>
              <th>Floating PL</th>
              <th>Concentration</th>
              <th>EOD swaps</th>
              <th>Platform</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 ? (
              <tr>
                <td colSpan={cols} className="text-center text-muted py-2">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              details.map((d, i) => {
                const source = d.source === 'LP' ? 'LP' : 'Client';
                const pl = d.originalPnl ?? 0;
                return (
                  <tr key={`${d.platformId}:${d.login}:${d.symbol}:${i}`}>
                    <td>
                      <span className={`badge ${source === 'LP' ? 'bg-warning-transparent' : 'bg-primary-transparent'}`}>{source}</span>
                    </td>
                    {showInstrument && <td className="text-start font-monospace">{d.symbol || '—'}</td>}
                    <td className="text-start">
                      {d.login != null && String(d.login) !== '' ? (
                        handlers ? (
                          <span
                            className="nop-clickable-cell d-inline-block"
                            title="Double click to filter by Login"
                            onDoubleClick={() => handlers.onFilterLogin(d.login, d.platformId)}
                          >
                            {d.login}
                          </span>
                        ) : (
                          d.login
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-start">
                      {d.uniqueId ? (
                        handlers ? (
                          <span
                            className="nop-clickable-cell d-inline-block"
                            title="Double click to filter by Unique ID"
                            onDoubleClick={() => handlers.onFilterUniqueId(d.uniqueId)}
                          >
                            {d.uniqueId}
                          </span>
                        ) : (
                          d.uniqueId
                        )
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="font-monospace">{formatNumber(d.netVolume, 2)}</td>
                    <td className="font-monospace">{formatNumber(d.averagePrice, digits)}</td>
                    <td className="font-monospace">
                      <span className={pnlClass(pl)}>{formatNumber(pl, 3)}</span>
                    </td>
                    <td className="font-monospace">{d.concentration != null ? `${formatNumber(d.concentration, 2)}%` : '—'}</td>
                    <td className="font-monospace">{(() => { const f = formatEodSwap(loginEodSwap(eodBySymbol, d)); return <span className={f.cls}>{f.text}</span>; })()}</td>
                    <td>{d.platform || '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
